import { Smartphone, RefreshCw, LayoutGrid, Users, Gift, Globe, ArrowUpRight } from "lucide-react";
import Seo from "@/lib/Seo";
import { mediaUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import Reveal from "@/components/Reveal";

const FEATURES = [
  { icon: RefreshCw, title: "+1000 questions bonus", text: "Une banque de questions en ligne, régulièrement enrichie, en plus des 500 cartes de la boîte." },
  { icon: LayoutGrid, title: "Multi-catégories", text: "Ville de Nice, football, Aiglons, culture : composez vos parties sur mesure." },
  { icon: Users, title: "Multi-joueurs", text: "Configurez vos joueurs et laissez l'application gérer les scores automatiquement." },
  { icon: Gift, title: "Incluse avec le jeu", text: "Gratuite pour tous les acheteurs — scannez le QR code de la boîte pour l'activer." },
  { icon: Globe, title: "Accessible partout", text: "Sur web, mobile et tablette, sans installation." },
];

export default function ApplicationPage() {
  const { settings, content } = useSite();
  return (
    <>
      <Seo title="Application compagnon" path="/application"
        description="L'application compagnon de Douzième Homme — Le Jeu : +1000 questions bonus, multi-catégories, scores automatiques. Incluse avec le jeu." />

      <section className="py-14 sm:py-20 lg:py-28" data-testid="app-page">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow mb-4">{content.app_eyebrow}</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight uppercase leading-[1.05] text-onyx">
              {content.app_title}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl">
              {content.app_text}
            </p>
            <a href={settings?.app_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="app-page-cta"
              className="mt-8 inline-flex items-center gap-2 bg-rouge text-white px-9 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover hover:-translate-y-0.5 transition-all">
              <Smartphone className="w-4 h-4" /> Accéder à l'application <ArrowUpRight className="w-4 h-4" />
            </a>
            <p className="mt-4 text-xs text-muted-foreground">L'application s'ouvre dans un nouvel onglet.</p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-4 bg-med/10 -rotate-2" aria-hidden="true" />
              <img src={mediaUrl("/api/uploads/app-phone-bg.png")} alt="Application Douzième Homme sur smartphone"
                className="relative w-full max-w-sm mx-auto" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-onyx text-cream" data-testid="app-features">
        <div className="container-x">
          <Reveal className="mb-12">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.1]">
              Ce que l'app ajoute à vos parties.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="app-features-grid">
            {FEATURES.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="h-full border border-cream/15 p-7 hover:border-rouge-light/60 transition-colors">
                  <Icon className="w-6 h-6 text-rouge-light mb-4" />
                  <h3 className="font-display text-lg font-bold mb-2">{title}</h3>
                  <p className="text-sm text-cream/65 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <a href={settings?.app_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="app-page-cta-bottom"
              className="inline-flex items-center gap-2 bg-white text-onyx px-9 py-4 text-sm font-bold uppercase tracking-wide hover:bg-cream transition-colors">
              <Smartphone className="w-4 h-4" /> Accéder à l'application
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
